import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { SectorIndustry } from '../../results/sidebar/services/sector-industry.service';
import { TechIntelCategory } from '../../results/sidebar/services/tech-intel-category.service';
import { AccountService, FeatureFlagService, IntercomService } from '../../shared/services';
import { LoadingWrapper } from '../../shared/decorators';
import { AuthenticationService } from './authentication.service';
import { RumService } from '../../shared/services/rum.service';

export const INITIAL_GLOBAL_LOADING_TAG: string = 'initial-global-loading-tag';

@Injectable({
    providedIn: 'root',
})
export class InitService {
    private readonly isInitializedSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);

    constructor(
        private readonly authenticationService: AuthenticationService,
        private readonly industry: SectorIndustry,
        private readonly techIntelCategoryService: TechIntelCategory,
        private readonly accountService: AccountService,
        private readonly featureFlagService: FeatureFlagService,
        private readonly rumService: RumService,
        private readonly intercomService: IntercomService,
    ) {
        this.authenticationService.loggedOut$.subscribe(() => {
            this.isInitializedSubject.next(false);
        })
    }

    get isInitialized$(): Observable<boolean> {
        return this.isInitializedSubject.asObservable();
    }

    /**
     * This method can be used for requests that has to be called when
     * user is logged and before he navigated to cabinet area,
     * e.g. some request that will be used to render html based on user addons
     *
     * IMPORTANT: this request is used inside guard, and can be called multiple times,
     * consider this when building init method, avoid multiple requests and cancel if they will called multiple times
     */

    @LoadingWrapper(INITIAL_GLOBAL_LOADING_TAG)
    initiate(): Observable<any> {
        return this.authenticationService.initUserData().pipe(
          take(1),
          switchMap(() => forkJoin(([
              this.industry.init(),
              this.accountService.init(),
              this.techIntelCategoryService.init(),
              this.featureFlagService.areFlagsInitialized().pipe(filter(Boolean), take(1)),
              this.rumService.initSession(),
          ])).pipe(
            switchMap((result) => this.intercomService.init().pipe(map(() => result))),
          )),
          tap(() => this.isInitializedSubject.next(true)),
        );
    }

}
